import { AfterViewInit, Directive, ElementRef, Injector, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormArray,
  FormGroup,
  TouchedChangeEvent,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { getAllErrors } from './form-errors';

@Directive()
export abstract class SubformComponentBase<T>
  implements ControlValueAccessor, OnDestroy, AfterViewInit
{
  abstract form: FormGroup | FormArray;
  currentControl: AbstractControl | null = null;
  value: T;
  onTouched: Function = () => {};
  onChange: Function = (value: T | null) => {};
  subscriptions: Subscription[] = [];
  elem: ElementRef;

  constructor(private injector: Injector) {
    this.value = null as T;
    this.elem = this.injector.get<ElementRef>(ElementRef);
  }

  getCurrentControl() {
    if (this.currentControl) {
      return this.currentControl;
    }

    let container: ControlContainer;
    try {
      container = this.injector.get(ControlContainer, undefined, { skipSelf: false });
    } catch (e) {
      container = null as unknown as ControlContainer;
    }

    if (container && container.control) {
      this.currentControl = container.control;
    }

    return this.currentControl;
  }

  ngAfterViewInit(): void {
    const currentControl = this.getCurrentControl();
    if (currentControl) {
      const allEventSubscription = currentControl.events.subscribe((event) => {
        if (event instanceof TouchedChangeEvent) {
          this.form.markAllAsTouched();
        }
      });
      this.subscriptions.push(allEventSubscription);
    } else {
      console.warn('No current control!');
    }
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  registerOnChange(onChange: Function) {
    this.onChange = onChange;
    const sub = this.form.valueChanges.subscribe((val) => {
      this.value = val;
      onChange(val);
    });
    this.subscriptions.push(sub);
  }

  registerOnTouched(onTouched: Function) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  writeValue(value: T | null) {
    if (value) {
      this.value = value;
      this.form.reset(undefined, { emitEvent: false });
      this.form.patchValue(value as any, { emitEvent: false });
      return;
    }

    this.value = value as T;
    this.form.reset(undefined, { emitEvent: false });
  }

  validate(control: AbstractControl) {
    if (this.form.valid) {
      return null;
    }

    return {
      nestedFormFails: true,
    };
  }

  getAllErrors(): Record<string, unknown> {
    return getAllErrors(this.form);
  }
}
